import FlatfileImporter from "@flatfile/adapter";
import { IFieldBase } from "@flatfile/adapter/build/main/interfaces";
import {
  ICollaboratorSchema,
  WSDataTypes
} from "@wingspanhq/payments/dist/interfaces";
import { useUserId } from "../../../query/hooks/helpers";
import {
  useCollaboratorsQuery,
  useListCollaboratorSettingsAdditionalData
} from "../../../query/payments/queries";
import { getVisibleCollaboratorsSortByCreatedAt } from "../../../query/payments/selectors";
import { useUserProfile } from "../../../query/users/queries";
import {
  validateAddressLine,
  validateEIN,
  validateEmail,
  textNumbersSymbolsExt,
  validateName,
  validatePhone,
  validateSSN,
  validateZipCode
} from "../../../utils/validators";
import { flatfileTheme } from "../../../utils/flatfileTheme";
import { defaultCollaboratorGroupOption } from "./BulkUploadCollaboratorsStep2";
import { getMatchingCollaborators } from "./collaboratorUtils";
import { COUNTRY_OPTIONS, US_STATES_OPTIONS } from "../../../constants/user";
import { BUSINESS_TYPE_OPTIONS } from "../../../Settings/components/SettingsUpdateForms/BusinessTypeForm";
import { faker } from "@faker-js/faker";
import { convertDateToMidday } from "../../../utils/dates";
import isValid from "date-fns/isValid";
import format from "date-fns/format";
import isFuture from "date-fns/isFuture";

const apiKey = process.env.REACT_APP_FLATFILE_API_KEY || "";

export const getMatchingGroupedCollaborators = (
  collaborators: ICollaboratorSchema[],
  collaboratorGroupId: string
) => {
  return collaborators.filter(
    collaborator => collaborator.collaboratorGroupId === collaboratorGroupId
  );
};

export interface BulkUploadCollaboratorsImporterOptions {
  onClose?: () => void;
  withFormW9?: boolean;
}

export const useBulkUploadCollaboratorsImporter = (
  collaboratorGroup: {
    label: string;
    value: string;
  },
  options?: BulkUploadCollaboratorsImporterOptions
) => {
  const selectedCollaboratorGroup =
    collaboratorGroup.value === defaultCollaboratorGroupOption.value
      ? null
      : collaboratorGroup;
  const userId = useUserId();
  const user = useUserProfile(userId);
  const collaboratorsQuery = useCollaboratorsQuery();
  const existingCollaborators = getVisibleCollaboratorsSortByCreatedAt(
    collaboratorsQuery.data || []
  );
  const collaboratorSettingsAdditionalDataQuery = useListCollaboratorSettingsAdditionalData(
    {
      retry: false
    }
  );
  const additionalData = collaboratorSettingsAdditionalDataQuery.data || [];

  const importer = new FlatfileImporter(apiKey, {
    ...flatfileTheme,
    type: "Collaborators",
    title: "Bulk upload collaborators to Wingspan",
    autoDetectHeaders: true,
    devMode: !(process.env.REACT_APP_ENV === "production"),
    managed: true,
    disableManualInput: true,
    displayEncoding: false,
    fields: [
      {
        label: "Email",
        key: "email",
        validators: [
          {
            validate: "required_without_all",
            fields: ["contractorId", "collaboratorId"]
          },
          { validate: "unique" }
        ]
      },
      {
        label: "External Collaborator ID",
        key: "contractorId",
        alternates: ["Contractor ID", "Collaborator ID"],
        validators: [
          {
            validate: "required_without_all",
            fields: ["email", "collaboratorId"]
          },
          { validate: "unique" }
        ]
      },
      {
        label: "Collaborator ID",
        key: "collaboratorId",
        validators: [
          {
            validate: "required_without_all",
            fields: ["email", "contractorId"]
          },
          { validate: "unique" }
        ]
      },
      {
        label: "First Name",
        key: "firstName"
      },
      {
        label: "Last Name",
        key: "lastName"
      },
      {
        label: "Phone number",
        key: "phoneNumber"
      },

      {
        label: "W-9 First Name",
        key: "w9FirstName"
      },
      {
        label: "W-9 Last Name",
        key: "w9LastName"
      },
      {
        label: "Date of Birth",
        key: "dob"
      },
      {
        label: "W-9 Legal Business Name",
        key: "legalBusinessName"
      },
      {
        label: "W-9 Company Structure",
        key: "companyStructure",
        type: "select",
        options: BUSINESS_TYPE_OPTIONS,
        validators: [
          {
            validate: "required_with",
            fields: ["legalBusinessName"]
          }
        ]
      },
      {
        label: "Employee Identification Number",
        key: "ein",
        alternates: ["EIN", "ein"]
      },
      {
        label: "Social Security Number",
        key: "ssn",
        alternates: ["SSN", "ssn"]
      },

      {
        label: "Address Line1",
        key: "addressLine1",
        validators: [
          options?.withFormW9
            ? {
                validate: "required"
              }
            : {
                validate: "required_with",
                fields: ["city", "state", "postalCode", "country"]
              }
        ]
      },
      {
        label: "Address Line2",
        key: "addressLine2"
      },
      {
        label: "City",
        key: "city",
        validators: [
          options?.withFormW9
            ? {
                validate: "required"
              }
            : {
                validate: "required_with",
                fields: ["addressLine1", "state", "postalCode", "country"]
              }
        ]
      },
      {
        label: "State",
        key: "state",
        type: "select",
        options: US_STATES_OPTIONS,
        validators: [
          options?.withFormW9
            ? {
                validate: "required"
              }
            : {
                validate: "required_with",
                fields: ["addressLine1", "city", "postalCode", "country"]
              }
        ]
      },
      {
        label: "Postal Code",
        key: "postalCode",
        validators: [
          options?.withFormW9
            ? {
                validate: "required"
              }
            : {
                validate: "required_with",
                fields: ["addressLine1", "city", "state", "country"]
              }
        ]
      },
      {
        label: "Country",
        key: "country",
        type: "select",
        options: COUNTRY_OPTIONS,
        validators: [
          options?.withFormW9
            ? {
                validate: "required"
              }
            : {
                validate: "required_with",
                fields: ["addressLine1", "city", "state", "postalCode"]
              }
        ]
      },
      ...additionalData.map(
        (field): IFieldBase => ({
          label: field.name,
          key: field.key,
          type: field.type === WSDataTypes.Boolean ? "checkbox" : "string",
          validators: field.required ? [{ validate: "required" }] : undefined
        })
      )
    ]
  });

  importer.registerRecordHook(async (record: any) => {
    const matchingCollaborators = getMatchingCollaborators(
      existingCollaborators,
      {
        email: record.email,
        contractorId: record.contractorId
      }
    );
    let matchingGroupedCollaborators = [];
    if (selectedCollaboratorGroup) {
      matchingGroupedCollaborators = getMatchingGroupedCollaborators(
        matchingCollaborators,
        selectedCollaboratorGroup.value
      );
    }

    const getValidationInfo = () => {
      let info: Array<{ level: string; message: string }> = [];
      if (selectedCollaboratorGroup) {
        if (matchingGroupedCollaborators.length > 0) {
          info = [
            {
              level: "error",
              message:
                "A collaborator already exists with this ID and collaborator group"
            }
          ];
        } else {
          if (matchingCollaborators.length > 0) {
            const unGroupedCollaborators = matchingCollaborators.filter(
              collaborator => !collaborator.collaboratorGroupId
            );
            if (unGroupedCollaborators.length > 0) {
              info = [
                {
                  level: "warning",
                  message:
                    unGroupedCollaborators.length === 1
                      ? `This collaborator already exists and will be added to ${selectedCollaboratorGroup.label}`
                      : `Multiple collaborators exist with this ID. The most recently created collaborator will be added to ${selectedCollaboratorGroup.label}`
                }
              ];
            }
          }
        }
      } else {
        if (
          matchingCollaborators.length > 0 &&
          !(record.addressLine1 || record.ssn || record.ein)
        ) {
          info = [
            {
              level: "warning",
              message: "Collaborator already exists and will NOT be updated"
            }
          ];
        }
      }
      return info;
    };

    const output: any = {};
    if (!record.email && !record.contractorId) {
      const error = {
        info: [
          {
            message: "Please provide any one identifier",
            level: "error"
          }
        ]
      };
      output.email = {
        value: record.email,
        ...error
      };
      output.contractorId = {
        value: record.contractorId,
        ...error
      };
    }

    if (record.firstName) {
      const isValidFirstName = validateName(record.firstName);
      if (!isValidFirstName) {
        output.firstName = {
          value: record.firstName,
          info: [
            {
              message: "Must be A-Z characters only",
              level: "error"
            }
          ]
        };
      }
    }
    if (record.lastName) {
      const isValidLastName = validateName(record.lastName);
      if (!isValidLastName) {
        output.lastName = {
          value: record.lastName,
          info: [
            {
              message: "Must be A-Z characters only",
              level: "error"
            }
          ]
        };
      }
    }
    if (record.email) {
      const isValidEmail = validateEmail(record.email);
      if (isValidEmail) {
        output.email = {
          value: record.email.toLowerCase()
        };
      } else {
        output.email = {
          value: record.email,
          info: [
            {
              message: "Invalid email address",
              level: "error"
            }
          ]
        };
      }
    } else {
      output.email = {
        value: faker.internet
          .email(record.firstName, record.lastName, "temp-ws.com")
          .toLowerCase(),
        info: [
          {
            message: "Generated temporary email",
            level: "info"
          }
        ]
      };
    }

    if (record.email && matchingCollaborators.length > 0) {
      output.email = {
        value: record.email,
        info: getValidationInfo()
      };
    }
    if (record.contractorId && matchingCollaborators.length > 0) {
      output.contractorId = {
        value: record.contractorId,
        info: getValidationInfo()
      };
    }

    if (record.phoneNumber) {
      const phoneNumber = record.phoneNumber?.replace(/[^0-9]/g, "");
      const isValidPhone = validatePhone(phoneNumber);
      if (!isValidPhone) {
        output.phoneNumber = {
          value: phoneNumber,
          info: [
            {
              message: "Invalid phone number",
              level: "error"
            }
          ]
        };
      }
    }

    if (record.ssn) {
      const isValidSSN = validateSSN(record.ssn);
      if (!isValidSSN) {
        output.ssn = {
          value: record.ssn,
          info: [
            {
              message: "Invalid SSN",
              // FIXME(@Anesh): For testing purpose: change it to error later
              level: "info"
            }
          ]
        };
      }
    }

    if (record.ein) {
      const isValidEIN = validateEIN(record.ein);
      if (!isValidEIN) {
        output.ein = {
          value: record.ein,
          info: [
            {
              message: "Invalid Employee Identification Number",
              // FIXME(@Anesh): For testing purpose: change it to error later
              level: "info"
            }
          ]
        };
      }
    }

    if (record.legalBusinessName) {
      const isValidLegalBusinessName = textNumbersSymbolsExt(
        record.legalBusinessName
      );
      if (!isValidLegalBusinessName) {
        output.legalBusinessName = {
          value: record.legalBusinessName,
          info: [
            {
              message:
                "May contain only characters: a-z, A-Z, 0-9, #, ', ., &, - and inner spaces",
              level: "error"
            }
          ]
        };
      }
    }

    if (record.dob) {
      const dob = convertDateToMidday(new Date(record.dob));
      if (isValid(dob)) {
        output.dob = {
          value: format(dob, "yyyy-MM-dd"),
          info: isFuture(dob)
            ? [
                {
                  message: "Date of Birth cannot be in the future.",
                  level: "error"
                }
              ]
            : []
        };
      } else {
        output.dob = {
          value: record.dob,
          info: [
            {
              message: "Please check that the date is formatted YYYY-MM-DD.",
              level: "error"
            }
          ]
        };
      }
    }

    if (record.w9FirstName) {
      const isValidName = validateName(record.w9FirstName);
      if (!isValidName) {
        output.w9FirstName = {
          value: record.w9FirstName,
          info: [
            {
              message: "Invalid W-9 First Name",
              level: "error"
            }
          ]
        };
      }
    }

    if (record.w9LastName) {
      const isValidName = validateName(record.w9LastName);
      if (!isValidName) {
        output.w9LastName = {
          value: record.w9LastName,
          info: [
            {
              message: "Invalid W-9 Last Name",
              level: "error"
            }
          ]
        };
      }
    }

    if (record.addressLine1) {
      const isValid = textNumbersSymbolsExt(record.addressLine1);
      if (!isValid) {
        output.addressLine1 = {
          value: record.addressLine1,
          info: [
            {
              message:
                "May contain only characters: a-z, A-Z, 0-9, #, ', ., &, -, inner spaces and accented characters",
              level: "error"
            }
          ]
        };
      }
    }

    if (record.addressLine2) {
      const isValid = textNumbersSymbolsExt(record.addressLine2);
      if (!isValid) {
        output.addressLine2 = {
          value: record.addressLine2,
          info: [
            {
              message:
                "May contain only characters: a-z, A-Z, 0-9, #, ', ., &, -, inner spaces and accented characters",
              level: "error"
            }
          ]
        };
      }
    }

    if (record.city) {
      const isValid = validateName(record.city);
      if (!isValid) {
        output.city = {
          value: record.city,
          info: [
            {
              message: "Invalid City",
              level: "error"
            }
          ]
        };
      }
    }

    if (record.postalCode) {
      const isValid = validateZipCode(record.postalCode);
      if (!isValid) {
        output.postalCode = {
          value: record.postalCode,
          info: [
            {
              message: "Invalid Postal code",
              // FIXME(@Anesh): For testing purpose: change it to error later
              level: "info"
            }
          ]
        };
      }
    }
    return output;
  });

  importer.setCustomer({
    userId: userId,
    name: `${user.data?.profile.firstName} ${user.data?.profile.lastName}`
  });

  importer.on("close", () => {
    options?.onClose?.();
  });

  return async () => {
    try {
      const results = await importer.requestDataFromUser();
      await importer.close();
      return results;
    } catch (error) {
      console.info(error || "window close");
      throw error;
    }
  };
};
